import React, {useCallback, useState} from 'react';

import {useEventTracking} from 'src/hooks/useEventTracking';

import Group from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import {Tooltip} from '@pb/kit-component-popups';
import Tabs, {Tab} from '@pb/kit-component-tabs-v2';
import Typography from '@pb/kit-component-typography';

import {useSsoSessionStorage} from '../../hooks/use-sso-session-storage';
import {StepFooter} from '../step-footer/step-footer';

import {ManualEntryTab} from './components/manual-entry-tab';
import {MetadataUrlTab} from './components/metadata-url-tab';
import {MetadataXmlTab} from './components/metadata-xml-tab';

import {
    infoIcon,
    titleRow,
    uploadMetadataContainer,
} from './upload-metadata.module.styl';

import type {ManualTabData} from './components/manual-entry-tab';
import type {UrlTabData} from './components/metadata-url-tab';
import type {XmlTabData} from './components/metadata-xml-tab';

type UploadMetadataMethod = 'url' | 'file' | 'manual';

type UploadMetadataData = {
    method: UploadMetadataMethod,
    url: UrlTabData,
    file: XmlTabData,
    manual: ManualTabData,
};

const INITIAL_DATA: UploadMetadataData = {
    method: 'url',
    url: {url: '', loginUrl: '', certificate: '', valid: false},
    file: {fileName: null, content: null, loginUrl: '', certificate: '', valid: false},
    manual: {loginUrl: '', certificate: '', valid: false},
};

type UploadMetadataProps = {
    onContinue: () => unknown,
    onBack: () => void,
    saving?: boolean,
};

export const UploadMetadata: React.FC<UploadMetadataProps> = ({onContinue, onBack, saving}) => {
    const [metadataData, setMetadataData] = useSsoSessionStorage<UploadMetadataData>('uploadMetadata', INITIAL_DATA);
    const [selectedProvider] = useSsoSessionStorage<string | null>('selectedProvider', null);
    const [isBusy, setBusy] = useState<boolean>(false);
    const {metadataSelect} = useEventTracking();

    const handleTabClick = useCallback((method: string) => {
        const m = method as UploadMetadataMethod;
        metadataSelect({method: m});
        const validTab = [metadataData.url, metadataData.file, metadataData.manual].find(t => t.valid);
        if (validTab) {
            const {loginUrl: validLoginUrl, certificate: validCertificate} = validTab;
            setMetadataData({
                ...metadataData,
                method: m,
                url: {...metadataData.url, loginUrl: validLoginUrl, certificate: validCertificate, valid: true},
                file: {...metadataData.file, loginUrl: validLoginUrl, certificate: validCertificate, valid: true},
                manual: {loginUrl: validLoginUrl, certificate: validCertificate, valid: true},
            });
        } else {
            setMetadataData({...INITIAL_DATA, method: m});
        }
    }, [metadataData, metadataSelect, setMetadataData]);

    const handleUrlChange = useCallback((url: UrlTabData) => {
        if (url.valid) {
            setMetadataData({
                ...metadataData,
                url,
                file: {...metadataData.file, loginUrl: url.loginUrl, certificate: url.certificate, valid: true},
                manual: {loginUrl: url.loginUrl, certificate: url.certificate, valid: true},
            });
        } else if (!url.loginUrl && !url.certificate) {
            setMetadataData({...metadataData, url, file: INITIAL_DATA.file, manual: INITIAL_DATA.manual});
        } else {
            setMetadataData({...metadataData, url});
        }
    }, [metadataData, setMetadataData]);

    const handleXmlChange = useCallback((file: XmlTabData) => {
        if (file.valid) {
            setMetadataData({
                ...metadataData,
                file,
                url: {...metadataData.url, loginUrl: file.loginUrl, certificate: file.certificate, valid: true},
                manual: {loginUrl: file.loginUrl, certificate: file.certificate, valid: true},
            });
        } else if (!file.loginUrl && !file.certificate) {
            setMetadataData({...metadataData, file, url: INITIAL_DATA.url, manual: INITIAL_DATA.manual});
        } else {
            setMetadataData({...metadataData, file});
        }
    }, [metadataData, setMetadataData]);

    const handleManualChange = useCallback((manual: ManualTabData) => {
        if (manual.valid) {
            setMetadataData({
                ...metadataData,
                manual,
                url: {...metadataData.url, loginUrl: manual.loginUrl, certificate: manual.certificate, valid: true},
                file: {...metadataData.file, loginUrl: manual.loginUrl, certificate: manual.certificate, valid: true},
            });
        } else if (!manual.loginUrl && !manual.certificate) {
            setMetadataData({...metadataData, manual, url: INITIAL_DATA.url, file: INITIAL_DATA.file});
        } else {
            setMetadataData({...metadataData, manual});
        }
    }, [metadataData, setMetadataData]);

    const handleContinue = useCallback(async () => {
        setBusy(true);
        try {
            await onContinue();
        } finally {
            setBusy(false);
        }
    }, [onContinue, setBusy]);

    const isValid = metadataData[metadataData.method].valid;

    return (
        <Group direction="v" gap="l" className={uploadMetadataContainer}>
            <Group direction="v" gap="xs">
                <div className={titleRow}>
                    <Typography variant="heading-level-1">
                        Upload IdP Metadata
                    </Typography>
                    <Tooltip
                        placement="bottom"
                        color="light"
                        anchor={({anchorRef, getAnchorProps}) => (
                            <span
                                ref={anchorRef}
                                className={infoIcon}
                                {...getAnchorProps()}>
                                <Icon type={Icons.INFO} size="Size_16"/>
                            </span>
                        )}>
                        <span>
                            Metadata is an XML file that has your company
                            <br/>
                            login information for PitchBook to connect to.
                        </span>
                    </Tooltip>
                </div>
                <Typography variant="body-level-1">
                    Upload the SAML metadata XML file from your identity provider.
                </Typography>
            </Group>

            <Tabs tabsId="upload-metadata-tabs" tabPanelId="upload-metadata-panel" value={metadataData.method}>
                <Tab value="url" caption="Metadata URL" onClick={() => handleTabClick('url')}/>
                <Tab value="file" caption="Metadata XML File" onClick={() => handleTabClick('file')}/>
                <Tab value="manual" caption="Manual Entry" onClick={() => handleTabClick('manual')}/>
            </Tabs>

            {metadataData.method === 'url' ? (
                <MetadataUrlTab data={metadataData.url} onChange={handleUrlChange} providerId={selectedProvider}/>
            ) : null}
            {metadataData.method === 'file' ? (
                <MetadataXmlTab data={metadataData.file} onChange={handleXmlChange} providerId={selectedProvider}/>
            ) : null}
            {metadataData.method === 'manual' ? (
                <ManualEntryTab data={metadataData.manual} onChange={handleManualChange} providerId={selectedProvider}/>
            ) : null}

            <StepFooter
                onContinue={handleContinue}
                onBack={onBack}
                continueDisabled={!isValid}
                busy={isBusy || saving}
            />
        </Group>
    );
};
